import {ReactElement, useContext, useEffect} from 'react';

import {useQueryClient} from '@tanstack/react-query';

import {UserContext} from '../UserContext/UserContext';

import {CompanyContext} from './CompanyContext';
import {companySerializer} from './companySerializer';
import {CultureDashboardFilters} from '../../features/company/culture/dashboard/hooks/useCulturalDashboardPage';
import {CultureInformation} from '../../features/company/culture/dashboard/types/CultureInformationTypes';
import {Surveys} from '../../features/company/culture/surveys/types/SurveyTypes';
import {
  Benefit,
  Company,
  CompanyAdminsResponse,
  CompanyBenefitsResponse,
  CompanyDepartmentsResponse,
  Department,
} from '../../features/company/profile/types';
import {
  CompanyJobPostingsResponse,
  JobPosting,
} from '../../features/company/recruiting/job-postings/types/JobPostingsTypes';
import {useApiRead} from '../../hooks/useApiRead';
import {useTrackingEvents} from '../../hooks/useTrackingEvents';
import {GetRoutes} from '../../lib/api/GET/GetRoutes';
import {UseQueryResponse} from '../../lib/api/v1/types';
import CultureInformationSerializer from '../../serializers/CultureInformationSerializers';
import {CompanyProductConfiguration} from '../../types/CompanyProductConfiguration/CompanyProductConfiguration';
import {CompanyResourceFilter} from '../../types/CompanyResourceFilter';
import {CompanyLocation, CompanyLocationResponse} from '../../types/shared-types';
import {TrackingEvents} from '../../types/TrackingEvents';
import {CompanyAdminRoleData} from '../../types/User';

interface CompanyProviderResponse<T> extends UseQueryResponse<T> {
  invalidateCache: () => void;
}
export interface CompanyContextValues {
  company: CompanyProviderResponse<Company>;
  locations: CompanyProviderResponse<CompanyLocation[]>;
  benefits: CompanyProviderResponse<Benefit[]>;
  departments: CompanyProviderResponse<Department[]>;
  jobPostings: CompanyProviderResponse<JobPosting[]>;
  surveys: CompanyProviderResponse<Surveys>;
  productConfiguration: CompanyProviderResponse<CompanyProductConfiguration>;
  cultureInformation: CompanyProviderResponse<CultureInformation>;
  companyAdmins: CompanyProviderResponse<CompanyAdminsResponse>;
}

interface Props {
  children: ReactElement | ReactElement[];
}

export const CompanyProvider = ({children}: Props) => {
  const {currentUser} = useContext(UserContext);
  const queryClient = useQueryClient();
  const companyId = (currentUser?.role.data as CompanyAdminRoleData)?.companyId || '';
  const CONTEXT_ENABLED = !!companyId;
  const refetchOnWindowFocus = false;

  const {Version, Group} = TrackingEvents;
  const {trackGroup} = useTrackingEvents({
    version: Version.v1,
  });

  const company = useApiRead<Company>({
    path: GetRoutes.company(companyId),
    camelCaseOverride: companySerializer,
    options: {
      queryKey: ['company'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  useEffect(() => {
    if (company.isLoading || company.error || !company.data?.name) return;

    trackGroup({
      name: Group.Company,
      identifier: companyId,
      properties: {
        name: company.data.name,
      },
    });
  }, [company.isLoading, company.data, company.error, trackGroup, companyId, Group.Company]);

  const locations = useApiRead<CompanyLocationResponse>({
    path: GetRoutes.companyResource(companyId, CompanyResourceFilter.Locations),
    options: {
      queryKey: ['locations'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const benefits = useApiRead<CompanyBenefitsResponse>({
    path: GetRoutes.companyResource(companyId, CompanyResourceFilter.Benefits),
    options: {
      queryKey: ['benefits'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const departments = useApiRead<CompanyDepartmentsResponse>({
    path: GetRoutes.companyResource(companyId, CompanyResourceFilter.Departments),
    options: {
      queryKey: ['departments'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const jobPostings = useApiRead<CompanyJobPostingsResponse>({
    path: GetRoutes.companyResource(companyId, CompanyResourceFilter.JobPostings),
    options: {
      queryKey: ['job-postings'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const surveys = useApiRead<Surveys>({
    path: GetRoutes.companyResource(companyId, CompanyResourceFilter.Surveys),
    options: {
      queryKey: ['surveys'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const productConfiguration = useApiRead<CompanyProductConfiguration>({
    path: GetRoutes.companyProductConfiguration(companyId),
    options: {
      queryKey: ['company-product-configuration'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const cultureInformation = useApiRead<CultureInformation | undefined>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.CultureInformation, companyId),
    camelCaseOverride: CultureInformationSerializer.toObject,
    options: {
      queryKey: ['company-culture-information'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  const companyAdmins = useApiRead<CompanyAdminsResponse>({
    path: GetRoutes.companyAdmins(companyId),
    options: {
      queryKey: ['company-admins'],
      enabled: CONTEXT_ENABLED,
      refetchOnWindowFocus,
    },
  });

  return (
    <CompanyContext.Provider
      value={{
        company: {
          data: company.data,
          error: company.error,
          isLoading: company.isLoading,
          refetch: company.refetch,
          isPending: company.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['company']}),
        },
        benefits: {
          data: benefits?.data?.companyBenefits,
          error: benefits.error,
          isLoading: benefits.isLoading,
          refetch: benefits.refetch as UseQueryResponse<Benefit[]>['refetch'],
          isPending: benefits.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['benefits']}),
        },
        locations: {
          data: locations?.data?.companyLocations,
          error: locations.error,
          isLoading: locations.isLoading,
          refetch: locations.refetch as UseQueryResponse<CompanyLocation[]>['refetch'],
          isPending: locations.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['locations']}),
        },
        departments: {
          data: departments?.data?.companyDepartments,
          error: departments.error,
          isLoading: departments.isLoading,
          refetch: departments.refetch as UseQueryResponse<Department[]>['refetch'],
          isPending: departments.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['departments']}),
        },
        jobPostings: {
          data: jobPostings?.data?.companyJobPostings,
          error: jobPostings.error,
          isLoading: jobPostings.isLoading,
          refetch: jobPostings.refetch as UseQueryResponse<JobPosting[]>['refetch'],
          isPending: jobPostings.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['job-postings']}),
        },
        surveys: {
          data: surveys.data,
          error: surveys.error,
          isLoading: surveys.isLoading,
          refetch: surveys.refetch as UseQueryResponse<Surveys>['refetch'],
          isPending: surveys.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['surveys']}),
        },
        productConfiguration: {
          data: productConfiguration.data,
          error: productConfiguration.error,
          isLoading: productConfiguration.isLoading,
          refetch: productConfiguration.refetch as UseQueryResponse<CompanyProductConfiguration>['refetch'],
          isPending: productConfiguration.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['company-product-configuration']}),
        },
        cultureInformation: {
          data: cultureInformation.data,
          error: cultureInformation.error,
          isLoading: cultureInformation.isLoading,
          refetch: cultureInformation.refetch as UseQueryResponse<CultureInformation>['refetch'],
          isPending: cultureInformation.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['company-culture-information']}),
        },
        companyAdmins: {
          data: companyAdmins.data,
          error: companyAdmins.error,
          isLoading: companyAdmins.isLoading,
          refetch: companyAdmins.refetch as UseQueryResponse<CompanyAdminsResponse>['refetch'],
          isPending: companyAdmins.isPending,
          invalidateCache: () => queryClient.invalidateQueries({queryKey: ['company-admins']}),
        },
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};
